import { useEffect, useRef, useState } from 'react';
import {
  ldClear, ldEntryCount, ldFormatLog, ldMark, ldSubscribe,
  loginDebugEnabled, loginDebugNoMotion, loginDebugNoTurnstile, startLoginDebugSampler,
} from '../../utils/ui/loginDebug';

/**
 * Sign-in blank-card recorder chip (KAN-916) -- rendered ONLY behind
 * `?logindebug=1` in a debug build (see loginDebug.js). Dev-only readout:
 * EXEMPT from the i18n-parity rule and from design tokens (raw-metric probe,
 * same exemption as ViewportDebugOverlay). The big clock is what a QuickTime
 * recording aligns to, so it is black-on-white and survives a blank page.
 */
const rootStyle = {
  position: 'fixed',
  top: 4,
  right: 4,
  zIndex: 999999,
  pointerEvents: 'none',
  maxWidth: '92vw',
  padding: '6px 8px',
  background: '#ffffff',
  color: '#000000',
  border: '1px solid #000000',
  borderRadius: 6,
  fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace',
  fontSize: 11,
  lineHeight: 1.4,
  willChange: 'transform',
};
const clockStyle = { fontSize: 22, fontWeight: 700, color: '#000000' };
const btnStyle = {
  pointerEvents: 'auto', marginRight: 4, padding: '2px 6px', fontSize: 11,
  fontFamily: 'inherit', border: '1px solid #000', background: '#f1f5f9', color: '#000', borderRadius: 4,
};
const preStyle = {
  pointerEvents: 'auto', margin: '6px 0 0', maxHeight: '50vh', maxWidth: '90vw', overflow: 'auto',
  fontSize: 10, whiteSpace: 'pre', background: '#f8fafc', color: '#000',
};

export default function LoginDebugOverlay() {
  const [enabled] = useState(loginDebugEnabled);
  const [count, setCount] = useState(ldEntryCount());
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const clockRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    startLoginDebugSampler();
    const unsub = ldSubscribe(setCount);
    let raf = 0;
    const t0 = performance.now();
    const tick = () => {
      if (clockRef.current) clockRef.current.textContent = `${Math.round(performance.now() - t0)} ms`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { unsub(); cancelAnimationFrame(raf); };
  }, [enabled]);

  if (!enabled) return null;

  const show = (next) => { setText(ldFormatLog()); setOpen(next); };
  const copy = async () => {
    const log = ldFormatLog();
    try {
      await navigator.clipboard.writeText(log);
    } catch {
      show(true);
    }
  };

  return (
    <div style={rootStyle} data-testid="login-debug-overlay">
      <div ref={clockRef} style={clockStyle}>0 ms</div>
      <div>
        logindebug n={count}
        {loginDebugNoMotion() && <b style={{ color: '#dc2626', marginLeft: 6 }}>NOMOTION</b>}
        {loginDebugNoTurnstile() && <b style={{ color: '#dc2626', marginLeft: 6 }}>NOTURNSTILE</b>}
      </div>
      <div style={{ marginTop: 4 }}>
        <button type="button" style={btnStyle} onClick={() => ldMark('manual')}>Mark</button>
        <button type="button" style={btnStyle} onClick={copy}>Copy</button>
        <button type="button" style={btnStyle} onClick={() => { ldClear(); setText(''); }}>Clear</button>
        <button type="button" style={btnStyle} onClick={() => show(!open)}>Show</button>
      </div>
      {open && <pre style={preStyle}>{text}</pre>}
    </div>
  );
}
